import { useEffect, useState } from "react";
import { Button, Checkbox, Modal, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { getAnnouncementsForCurrentUser } from "../../Requests/announcement-requests";
import {
	ANNOUNCEMENTS_POPUP_SHOWN_KEY,
	isUserLogged,
} from "../../utils/utilFunctions";
import { AnnouncementForUserDTO } from "../../Api";
import dayjs from "../../utils/dayjs-setup";

const READ_ANNOUNCEMENT_IDS_KEY = "announcementsReadIds";

function getReadIds(): string[] {
	try {
		return JSON.parse(localStorage.getItem(READ_ANNOUNCEMENT_IDS_KEY) ?? "[]");
	} catch {
		return [];
	}
}

function addReadId(id: string): void {
	const ids = getReadIds();
	if (ids.includes(id)) return;
	localStorage.setItem(READ_ANNOUNCEMENT_IDS_KEY, JSON.stringify([...ids, id]));
}

export const AnnouncementsPopup = () => {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);
	const [checkedIds, setCheckedIds] = useState<string[]>([]);
	const [closedThisSession, setClosedThisSession] = useState(false);
	const [unreadAnnouncements, setUnreadAnnouncements] = useState<
		AnnouncementForUserDTO[]
	>([]);

	const { isLoading } = useQuery(
		["getAnnouncementsForCurrentUser"],
		getAnnouncementsForCurrentUser,
		{
			enabled: isUserLogged(),
			refetchOnWindowFocus: false,
			onSuccess: (data) => {
				const readIds = getReadIds();
				setUnreadAnnouncements(
					(data ?? []).filter((a) => a.id && !readIds.includes(a.id))
				);
			},
		}
	);

	useEffect(() => {
		if (unreadAnnouncements.length > 0 && !isLoading && !closedThisSession) {
			if (localStorage.getItem(ANNOUNCEMENTS_POPUP_SHOWN_KEY) === "true")
				return;
			localStorage.setItem(ANNOUNCEMENTS_POPUP_SHOWN_KEY, "true");
			setCheckedIds([]);
			setVisible(true);
		}
	}, [unreadAnnouncements.length, isLoading, closedThisSession]);

	const handleClose = () => {
		checkedIds.forEach((id) => addReadId(id));
		setUnreadAnnouncements((prev) =>
			prev.filter((a) => !a.id || !checkedIds.includes(a.id))
		);
		setClosedThisSession(true);
		setVisible(false);
	};

	const toggleChecked = (id: string) => {
		setCheckedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	const formatDate = (d: Date | null | undefined) =>
		d ? dayjs(d).format("DD.MM.YYYY") : "";

	return (
		<Modal
			title={t("announcements.popup.title")}
			open={visible}
			onCancel={handleClose}
			footer={null}
			width={750}
		>
			<div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
				{unreadAnnouncements.map((a: AnnouncementForUserDTO) => (
					<div
						key={a.id}
						className="border border-gray-200 rounded p-3 bg-gray-50"
					>
						<Typography.Title level={5} className="mt-0 mb-2">
							{a.title ?? ""}
						</Typography.Title>
					<div
						className="text-gray-700 text-sm mb-2 prose prose-sm"
						dangerouslySetInnerHTML={{ __html: a.description ?? "" }}
					/>
						<div className="flex flex-wrap items-center justify-between gap-2">
							<Typography.Text type="secondary" className="text-xs">
								{t("announcements.validity")}: {formatDate(a.validFrom)} –{" "}
								{formatDate(a.validTo)}
							</Typography.Text>
							<Checkbox
								checked={a.id ? checkedIds.includes(a.id) : false}
								onChange={() => a.id && toggleChecked(a.id)}
							>
								{t("announcements.popup.markAsRead")}
							</Checkbox>
						</div>
					</div>
				))}
			</div>
			<div className="flex justify-end mt-4">
				<Button type="primary" onClick={handleClose}>
					{t("announcements.popup.close")}
				</Button>
			</div>
		</Modal>
	);
};
